import { createAuthAxios } from './auth';

const serverService = {
  /**
   * Gets QuePasa global configuration (Super admin only)
   * @param {number} accountId - Account ID
   * @returns {Promise<{quepasa_url: string, quepasa_default_user: string}>} QuePasa settings
   * @example
   * const config = await quepasa.service.server.config(1);
   */
  async config(accountId) {
    const http = createAuthAxios();
    const response = await http.get(`/api/v1/accounts/${accountId}/quepasa`);
    return response.data;
  },

  async typingPolicy(accountId, inboxId) {
    const http = createAuthAxios();
    const response = await http.get(
      `/api/v1/accounts/${accountId}/quepasa/typing_policy`,
      { params: { inbox_id: inboxId } }
    );
    return response.data;
  },

  /**
   * Checks WhatsApp server health status
   * @param {number} accountId - Account ID
   * @param {number} inboxId - Inbox ID
   * @returns {Promise<{state: string, status: string}>} Server health (Ready/Unverified/Disconnected)
   * @example
   * const health = await quepasa.service.server.health(1, 803);
   */
  async health(accountId, inboxId) {
    const http = createAuthAxios();
    const response = await http.get(
      `/api/v1/accounts/${accountId}/quepasa/server/health`,
      { params: { inbox_id: inboxId } }
    );
    return response.data;
  },

  /**
   * Checks if server is initialized (lightweight check)
   * @param {number} accountId - Account ID
   * @param {number} inboxId - Inbox ID
   * @returns {Promise<{initialized: boolean}>} Server initialization status
   * @example
   * const status = await quepasa.service.server.status(1, 803);
   */
  async status(accountId, inboxId) {
    const http = createAuthAxios();
    const response = await http.get(
      `/api/v1/accounts/${accountId}/quepasa/server/status`,
      { params: { inbox_id: inboxId } }
    );
    return response.data;
  },

  async info(accountId, inboxId) {
    const http = createAuthAxios();
    const response = await http.get(
      `/api/v1/accounts/${accountId}/quepasa/server/info`,
      { params: { inbox_id: inboxId } }
    );
    return response.data;
  },

  async create(accountId, inboxId, data) {
    const http = createAuthAxios();
    const response = await http.post(
      `/api/v1/accounts/${accountId}/quepasa/server/info`,
      { inbox_id: inboxId, ...data }
    );
    return response.data;
  },

  async update(accountId, inboxId, data) {
    const http = createAuthAxios();
    const response = await http.patch(
      `/api/v1/accounts/${accountId}/quepasa/server/info`,
      { inbox_id: inboxId, ...data }
    );
    return response.data;
  },

  async start(accountId, inboxId) {
    const http = createAuthAxios();
    const response = await http.post(
      `/api/v1/accounts/${accountId}/quepasa/server/start`,
      { inbox_id: inboxId }
    );
    return response.data;
  },

  async stop(accountId, inboxId) {
    const http = createAuthAxios();
    const response = await http.post(
      `/api/v1/accounts/${accountId}/quepasa/server/stop`,
      { inbox_id: inboxId }
    );
    return response.data;
  },

  async scan(accountId, inboxId) {
    const http = createAuthAxios();
    const response = await http.get(
      `/api/v1/accounts/${accountId}/quepasa/scan`,
      { params: { inbox_id: inboxId }, responseType: 'blob' }
    );
    return response.data;
  },

  async pairing(accountId, inboxId) {
    const http = createAuthAxios();
    const response = await http.post(
      `/api/v1/accounts/${accountId}/quepasa/pairing`,
      { inbox_id: inboxId }
    );
    return response.data;
  },
};

// Add help properties to each method
serverService.config.help =
  'config(accountId) - Gets QuePasa global configuration. Returns: {quepasa_url, quepasa_default_user, typing_global_enabled}.';
serverService.typingPolicy.help =
  'typingPolicy(accountId, inboxId) - Gets effective QuePasa typing hierarchy for the inbox. Returns: {global_enabled, account_enabled, inbox_enabled, effective_enabled, can_configure_inbox}.';
serverService.health.help =
  'health(accountId, inboxId) - Checks WhatsApp server health. Returns: {state, status} (Ready/Unverified/Disconnected)';
serverService.status.help =
  'status(accountId, inboxId) - Checks if server is initialized (lightweight). Returns: {initialized: boolean}';
serverService.info.help =
  'info(accountId, inboxId) - Gets QuePasa server info including webhooks. Returns full server configuration.';
serverService.create.help =
  'create(accountId, inboxId, data) - Initializes WhatsApp server in QuePasa. Creates new server instance.';
serverService.update.help =
  'update(accountId, inboxId, data) - Updates server configuration. Modifies existing settings.';
serverService.start.help =
  'start(accountId, inboxId) - Starts WhatsApp server. Sends /command?action=start to QuePasa.';
serverService.stop.help =
  'stop(accountId, inboxId) - Stops WhatsApp server. Sends /command?action=stop to QuePasa.';
serverService.scan.help =
  'scan(accountId, inboxId) - Gets QR code for WhatsApp pairing. Returns blob image.';
serverService.pairing.help =
  'pairing(accountId, inboxId) - Gets pairing code for WhatsApp authentication. Returns 8-digit code.';

export default serverService;
